import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/layout/Header";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <div className="min-h-dvh bg-gray-50">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-24">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t("dashboard.welcome", "Welcome")}, {user?.name?.split(" ")[0] || "Designer"}!
          </h1>
          <p className="mt-1 text-gray-600">
            {t("dashboard.subtitle", "Start designing your dream kitchen or continue where you left off.")}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="mb-12 grid gap-6 md:grid-cols-3">
          {/* New Project Card */}
          <Link
            to="/studio"
            className="group rounded-xl border-2 border-dashed border-gray-300 bg-white p-8 text-center transition-all hover:border-primary-500 hover:shadow-lg"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-200">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              {t("dashboard.newProject.title", "New Project")}
            </h3>
            <p className="text-gray-600">
              {t("dashboard.newProject.description", "Start designing a new kitchen from scratch")}
            </p>
          </Link>

          {/* Browse Catalog Card */}
          <Link
            to="/catalog"
            className="group rounded-xl bg-white p-8 text-center shadow-sm transition-all hover:shadow-lg"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600 transition-colors group-hover:bg-amber-200">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              {t("dashboard.catalog.title", "Browse Catalog")}
            </h3>
            <p className="text-gray-600">
              {t("dashboard.catalog.description", "Explore products from Niga, Teka, and Neolith")}
            </p>
          </Link>

          {/* View Inspiration Card */}
          <Link
            to="/inspiration"
            className="group rounded-xl bg-white p-8 text-center shadow-sm transition-all hover:shadow-lg"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 text-purple-600 transition-colors group-hover:bg-purple-200">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              {t("dashboard.inspiration.title", "Get Inspired")}
            </h3>
            <p className="text-gray-600">
              {t("dashboard.inspiration.description", "View stunning kitchen designs and renders")}
            </p>
          </Link>
        </div>

        {/* My Projects Section */}
        <div className="mb-12">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {t("dashboard.myProjects", "My Projects")}
            </h2>
            <Link
              to="/dashboard/projects"
              className="text-sm font-medium text-primary-600 hover:underline"
            >
              {t("dashboard.viewAll", "View all")}
            </Link>
          </div>

          {/* Empty State */}
          <div className="rounded-xl bg-white p-12 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {t("dashboard.noProjects.title", "No projects yet")}
            </h3>
            <p className="mb-4 text-gray-600">
              {t("dashboard.noProjects.description", "Start your first kitchen design project")}
            </p>
            <Link
              to="/studio"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 font-medium text-white transition-colors hover:bg-primary-700"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("dashboard.createProject", "Create Project")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
